<template>
  <div v-if="message" class="campaign-inactive">
    <Head>
      <body class="site--404" />
    </Head>
    <h2 :style="messageStyle">{{ message.headline }}</h2>
    <!-- nosem: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
    <h4 class="message-body" :style="messageStyle" v-html="message.description"></h4>
  </div>
  <Campaign v-if="!isLoading && state && state.config && !message" :key="state.config.campaign_id" :data="state" />
</template>

<script lang="ts">
import { computed, CSSProperties, defineComponent, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';
import Campaign from '@/components/Campaign.vue';
import { CampaignData } from '@/typings/interfaces/data/campaign';
import { useStore } from '@/store';
import { isSsr } from '@/utilities/Utilities';
import { ApiMessageData } from '@/typings/interfaces/api';

export default defineComponent({
  name: 'CampaignView',
  components: {
    Campaign
  },
  async serverPrefetch() {
    await this.getCampaignData();
    await nextTick();
  },
  setup() {
    const route = useRoute();
    const store = useStore();

    const message = ref<ApiMessageData | undefined>();
    const state = ref<CampaignData>((store.getters['campaign/data'] || {}) as CampaignData);
    const isLoading = ref(!store.getters['campaign/data']);

    const host = store.getters['utility/host'] ?? (typeof window !== 'undefined' ? window.location.hostname : '');
    //Below is for testing locally
    //const host = 'showcase.qav2.leadfamly.com'; //ONLY FOR TESTING

    const getCampaignData = async () => {
      const { fetchData } = useAxios<CampaignData>(
        `${ConfigData.apiEndpoint}/api/v1/campaign/view-slug${route.path.split('#')[0].split('&')[0]}`,
        {
          vue: 1,
          host
        }
      );

      try {
        const response = await fetchData();

        if (response) {
          store.commit('campaign/setData', response);
          state.value = response;
        }
      } catch (e) {
        // In case API responds with a message to show (e.g. 404) we need to display this.
        // This is done by accepting the message incoming & putting it in a ref that will display the data.
        if (e?.response?.data?.message?.description) {
          // TODO: Investigate if we can type this more strongly. Not sure if we can.
          message.value = e.response.data.message as ApiMessageData;
        }
        // In case API responds with a redirect we need to display handle this.
        if (e?.response?.data?.redirect && !isSsr()) {
          // eslint-disable-next-line security/detect-non-literal-fs-filename
          window.open(e?.response?.data?.redirect.url, e?.response?.data?.redirect.target);
        }

        // TODO: Implement some sort of error handling for when campaign cannot be loaded
      }

      isLoading.value = false;
    };

    if (isLoading.value && !isSsr()) {
      getCampaignData();
    }

    const messageStyle = computed<CSSProperties>(() => {
      if (message.value) {
        return {
          textAlign: message.value.alignment,
          ...(message.value.textColor && { color: message.value.textColor })
        };
      }

      return {};
    });

    return {
      state,
      isLoading,
      getCampaignData,
      message,
      messageStyle
    };
  }
});
</script>
